import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, type MemberProfile } from "../api";
import { Avatar } from "../components/Avatar";
import { Badge, Card, SectionHeading, Spinner, Tag } from "../components/ui";

export default function MembersPage() {
  const list = useQuery({
    queryKey: ["members"],
    queryFn: () => api.get<{ members: MemberProfile[] }>("/members"),
  });

  return (
    <div className="flex flex-col gap-10">
      <div className="flex items-end justify-between gap-4">
        <SectionHeading
          eyebrow="The club"
          title="Members"
          description="The readers around the table. Click any profile for a deeper look."
        />
        <Link to="/turns" className="text-sm text-rose hover:text-rose-dark underline whitespace-nowrap">
          View turn rotation →
        </Link>
      </div>

      {list.isLoading ? (
        <Spinner />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {(list.data?.members ?? []).map((m) => (
            <Link key={m.id} to={`/members/${m.id}`}>
              <MemberCard member={m} />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function MemberCard({ member }: { member: MemberProfile }) {
  return (
    <Card className="group h-full transition-shadow duration-300 hover:shadow-[0_4px_8px_rgba(61,43,61,0.06),_0_24px_60px_rgba(199,123,133,0.18)]">
      <div className="flex items-start gap-4">
        <Avatar name={member.name} email={member.email} hue={member.avatarHue} size="lg" />
        <div className="flex-1 min-w-0">
          <h3 className="font-serif text-xl text-plum truncate">
            {member.name ?? member.email.split("@")[0]}
          </h3>
          <p className="text-xs text-plum-mute truncate">{member.email}</p>
          {member.stats?.isCurrentPicker ? (
            <Badge tone="rose">Picking now</Badge>
          ) : null}
        </div>
      </div>

      {member.bio ? (
        <p className="text-sm text-plum-soft italic mt-4 line-clamp-2">{member.bio}</p>
      ) : null}

      {member.favoriteGenres.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 mt-4">
          {member.favoriteGenres.slice(0, 4).map((g) => (
            <Tag key={g}>{g}</Tag>
          ))}
        </div>
      ) : null}

      {member.stats ? (
        <div className="flex items-center gap-5 mt-5 pt-4 border-t border-cream-200">
          <Stat label="Picks" value={member.stats.booksPicked} />
          <Stat label="Notes" value={member.stats.notesLeft} />
          <Stat label="Ratings" value={member.stats.ratingsGiven} />
          <Stat label="Quotes" value={member.stats.bookmarksSaved} />
        </div>
      ) : null}
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-col items-center">
      <div className="font-serif text-xl text-plum leading-none">{value}</div>
      <div className="text-[0.6rem] uppercase tracking-[0.14em] text-plum-mute mt-1">{label}</div>
    </div>
  );
}
